import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Dashboard.css';

const COASTAL_CITIES = [
  { name: 'Mumbai', lat: 19.076, lon: 72.8777, state: 'Maharashtra' },
  { name: 'Chennai', lat: 13.0827, lon: 80.2707, state: 'Tamil Nadu' },
  { name: 'Kochi', lat: 9.9312, lon: 76.2673, state: 'Kerala' },
  { name: 'Goa (Panaji)', lat: 15.4909, lon: 73.8278, state: 'Goa' },
  { name: 'Visakhapatnam', lat: 17.6868, lon: 83.2185, state: 'Andhra Pradesh' },
  { name: 'Kolkata', lat: 22.5726, lon: 88.3639, state: 'West Bengal' },
];

const CONSERVATION_TIPS = [
  { icon: '🧴', title: 'Reduce Single-Use Plastic', desc: 'Carry reusable bags, bottles, and straws. Say no to plastic packaging.', impact: 'Saves ~700 plastic bags per person/year' },
  { icon: '🐟', title: 'Choose Sustainable Seafood', desc: 'Look for MSC certified fish. Avoid overexploited species.', impact: 'Reduces pressure on endangered fish stocks' },
  { icon: '🏖️', title: 'Join Beach Cleanups', desc: 'Participate in local cleanup drives or organize your own.', impact: '1 hour = ~10kg of waste removed' },
  { icon: '💧', title: 'Reduce Water Pollution', desc: 'Use eco-friendly soaps, avoid flushing chemicals, fix oil leaks.', impact: 'Prevents toxins from reaching waterways' },
  { icon: '🌿', title: 'Support Mangroves', desc: 'Mangroves protect coastlines and are nurseries for marine life.', impact: '1 hectare absorbs 3-5x more CO₂ than forests' },
  { icon: '📢', title: 'Spread Awareness', desc: 'Share ocean facts on social media. Educate friends and family.', impact: 'Awareness is the first step to change' },
];

const Dashboard = () => {
  const [weatherData, setWeatherData] = useState({});
  const [selectedCity, setSelectedCity] = useState(COASTAL_CITIES[0]);
  const [loading, setLoading] = useState(true);
  const [oceanStats, setOceanStats] = useState([]);
  const [oceanLoading, setOceanLoading] = useState(true);

  useEffect(() => {
    fetchOceanStats();
  }, []);

  useEffect(() => {
    fetchWeather(selectedCity);
  }, [selectedCity]);

  // Fetch real ocean/climate data from free APIs
  const fetchOceanStats = async () => {
    setOceanLoading(true);
    try {
      // Fetch multiple real datasets in parallel
      const [co2Res, tempRes, arcticRes] = await Promise.allSettled([
        axios.get('https://global-warming.org/api/co2-api'),
        axios.get('https://global-warming.org/api/temperature-api'),
        axios.get('https://global-warming.org/api/arctic-api'),
      ]);

      const stats = [];

      // CO2 Data — latest atmospheric CO2
      if (co2Res.status === 'fulfilled' && co2Res.value.data.co2) {
        const co2Data = co2Res.value.data.co2;
        const latest = co2Data[co2Data.length - 1];
        stats.push({
          icon: '🧪',
          label: 'Atmospheric CO₂',
          value: `${parseFloat(latest.cycle).toFixed(1)} ppm`,
          detail: `Measured ${latest.month}/${latest.year} — Mauna Loa Observatory`,
          source: 'NOAA GML'
        });
      }

      // Temperature Anomaly — global land-ocean temp
      if (tempRes.status === 'fulfilled' && tempRes.value.data.result) {
        const tempData = tempRes.value.data.result;
        const latest = tempData[tempData.length - 1];
        const anomaly = parseFloat(latest.station).toFixed(2);
        stats.push({
          icon: '🌡️',
          label: 'Temp. Anomaly',
          value: `+${anomaly}°C`,
          detail: `Global surface temp vs 1951-1980 avg (${latest.time})`,
          source: 'NASA GISTEMP'
        });
      }

      // Arctic Sea Ice
      if (arcticRes.status === 'fulfilled' && arcticRes.value.data.arcpicanomaly) {
        const arcticData = arcticRes.value.data.arcpicanomaly;
        const latest = arcticData[arcticData.length - 1];
        const extent = parseFloat(latest.value).toFixed(2);
        stats.push({
          icon: '🧊',
          label: 'Arctic Ice Anomaly',
          value: `${extent}M km²`,
          detail: `Sea ice extent anomaly (${latest.year})`,
          source: 'NSIDC'
        });
      }

      // Always add these computed/known stats
      stats.push({
        icon: '📐',
        label: 'Sea Level Rise Rate',
        value: '+3.6mm/yr',
        detail: 'Satellite altimetry 1993-present — accelerating',
        source: 'NASA Sea Level'
      });

      stats.push({
        icon: '🗑️',
        label: 'Ocean Plastic',
        value: '~170T tons',
        detail: 'Estimated plastic in oceans (PLOS ONE 2023)',
        source: 'PLOS ONE'
      });

      stats.push({
        icon: '🐟',
        label: 'Overfished Stocks',
        value: '37.7%',
        detail: 'Of assessed fish stocks overfished (FAO 2024)',
        source: 'FAO SOFIA 2024'
      });

      setOceanStats(stats);
    } catch (err) {
      console.log('Ocean stats error:', err);
      // Fallback with sourced data
      setOceanStats([
        { icon: '🌡️', label: 'Avg. Ocean Temp', value: '17.1°C', detail: 'Global avg sea surface temperature', source: 'NOAA' },
        { icon: '🧪', label: 'Atmospheric CO₂', value: '~424 ppm', detail: 'API unavailable — using latest known value', source: 'NOAA GML' },
        { icon: '📐', label: 'Sea Level Rise', value: '+3.6mm/yr', detail: 'Satellite altimetry 1993-present', source: 'NASA' },
        { icon: '🐟', label: 'Overfished Stocks', value: '37.7%', detail: 'FAO assessment 2024', source: 'FAO' },
      ]);
    } finally {
      setOceanLoading(false);
    }
  };

  const fetchWeather = async (city) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `https://api.open-meteo.com/v1/forecast?latitude=${city.lat}&longitude=${city.lon}&current=temperature_2m,relative_humidity_2m,wind_speed_10m,weather_code&daily=temperature_2m_max,temperature_2m_min,precipitation_sum&timezone=auto&forecast_days=7`
      );
      
      // Also fetch marine data for this city's coast
      const marineRes = await axios.get(
        `https://marine-api.open-meteo.com/v1/marine?latitude=${city.lat}&longitude=${city.lon}&current=wave_height,wave_period&daily=wave_height_max&timezone=auto&forecast_days=7`
      );
      
      setWeatherData({ ...response.data, marine: marineRes.data });
    } catch (err) {
      console.log('Weather API error:', err);
      try {
        // Fallback without marine data
        const response = await axios.get(
          `https://api.open-meteo.com/v1/forecast?latitude=${city.lat}&longitude=${city.lon}&current=temperature_2m,relative_humidity_2m,wind_speed_10m,weather_code&daily=temperature_2m_max,temperature_2m_min,precipitation_sum&timezone=auto&forecast_days=7`
        );
        setWeatherData(response.data);
      } catch (e) {
        console.log('Weather fallback error:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  const getWeatherEmoji = (code) => {
    if (!code && code !== 0) return '🌤️';
    if (code === 0) return '☀️';
    if (code <= 3) return '⛅';
    if (code <= 48) return '🌫️';
    if (code <= 67) return '🌧️';
    if (code <= 77) return '🌨️';
    if (code <= 82) return '🌧️';
    if (code <= 99) return '⛈️';
    return '🌤️';
  };

  const getWeatherLabel = (code) => {
    if (!code && code !== 0) return 'Clear';
    if (code === 0) return 'Clear Sky';
    if (code <= 3) return 'Partly Cloudy';
    if (code <= 48) return 'Foggy';
    if (code <= 67) return 'Rainy';
    if (code <= 77) return 'Snow';
    if (code <= 82) return 'Rain Showers';
    if (code <= 99) return 'Thunderstorm';
    return 'Clear';
  };

  const current = weatherData.current || {};
  const daily = weatherData.daily || {};
  const marine = weatherData.marine?.current || {};
  const marinDaily = weatherData.marine?.daily || {};

  return (
    <div className="dashboard-page">
      <div className="dashboard-container">
        <div className="dashboard-header">
          <span className="dashboard-badge">📊 Live Data</span>
          <h1>Ocean & Coastal Dashboard</h1>
          <p className="dashboard-subtitle">Real-time data from NOAA, NASA, FAO, and Open-Meteo APIs.</p>
        </div>

        {/* Ocean Health Stats — fetched from real APIs */}
        <section className="ocean-stats-section">
          <h2 className="section-title-dash">🌍 Global Ocean & Climate Data <span className="live-tag">● LIVE</span></h2>
          {oceanLoading ? (
            <div className="weather-loading">
              <div className="weather-spinner"></div>
              <p>Fetching real-time climate data from NOAA, NASA...</p>
            </div>
          ) : (
            <div className="ocean-stats-grid">
              {oceanStats.map((fact, i) => (
                <div key={i} className="ocean-stat-card">
                  <span className="stat-icon">{fact.icon}</span>
                  <div className="stat-value">{fact.value}</div>
                  <div className="stat-label">{fact.label}</div>
                  <div className="stat-detail">{fact.detail}</div>
                  <div className="stat-source">📡 {fact.source}</div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Live Weather */}
        <section className="weather-section">
          <h2 className="section-title-dash">🌊 Coastal Weather & Marine Conditions <span className="live-tag">● LIVE</span></h2>
          
          <div className="city-selector">
            {COASTAL_CITIES.map(city => (
              <button 
                key={city.name} 
                className={`city-tab ${selectedCity.name === city.name ? 'active' : ''}`}
                onClick={() => setSelectedCity(city)}
              >
                {city.name}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="weather-loading">
              <div className="weather-spinner"></div>
              <p>Fetching live weather for {selectedCity.name}...</p>
            </div>
          ) : (
            <div className="weather-content">
              <div className="current-weather-card">
                <div className="weather-main">
                  <span className="weather-emoji-large">{getWeatherEmoji(current.weather_code)}</span>
                  <div className="weather-temp">{Math.round(current.temperature_2m || 0)}°C</div>
                  <div className="weather-condition">{getWeatherLabel(current.weather_code)}</div>
                  <div className="weather-city">{selectedCity.name}, {selectedCity.state}</div>
                </div>
                <div className="weather-details">
                  <div className="weather-detail-item">
                    <span className="detail-icon">💧</span>
                    <span className="detail-label">Humidity</span>
                    <span className="detail-value">{current.relative_humidity_2m || '--'}%</span>
                  </div>
                  <div className="weather-detail-item">
                    <span className="detail-icon">💨</span>
                    <span className="detail-label">Wind</span>
                    <span className="detail-value">{Math.round(current.wind_speed_10m || 0)} km/h</span>
                  </div>
                  <div className="weather-detail-item">
                    <span className="detail-icon">🌊</span>
                    <span className="detail-label">Wave Height</span>
                    <span className="detail-value">{marine.wave_height || marinDaily.wave_height_max?.[0] || '--'} m</span>
                  </div>
                  <div className="weather-detail-item">
                    <span className="detail-icon">🔄</span>
                    <span className="detail-label">Wave Period</span>
                    <span className="detail-value">{marine.wave_period || '--'} s</span>
                  </div>
                </div>
              </div>

              {/* 7-day forecast */}
              <div className="forecast-section">
                <h3>7-Day Forecast</h3>
                <div className="forecast-grid">
                  {daily.time?.slice(0, 7).map((date, i) => (
                    <div key={i} className="forecast-day">
                      <div className="forecast-date">
                        {i === 0 ? 'Today' : new Date(date).toLocaleDateString('en-IN', { weekday: 'short' })}
                      </div>
                      <div className="forecast-temp">
                        <span className="temp-high">{Math.round(daily.temperature_2m_max?.[i] || 0)}°</span>
                        <span className="temp-low">{Math.round(daily.temperature_2m_min?.[i] || 0)}°</span>
                      </div>
                      <div className="forecast-rain">{daily.precipitation_sum?.[i] || 0}mm</div>
                      {marinDaily.wave_height_max?.[i] && (
                        <div className="forecast-wave">🌊 {marinDaily.wave_height_max[i]}m</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Conservation Tips */}
        <section className="tips-section">
          <h2 className="section-title-dash">♻️ What You Can Do Today</h2>
          <div className="tips-grid">
            {CONSERVATION_TIPS.map((tip, i) => (
              <div key={i} className="tip-card">
                <div className="tip-icon">{tip.icon}</div>
                <h3>{tip.title}</h3>
                <p>{tip.desc}</p>
                <div className="tip-impact">
                  <span>📈</span> {tip.impact}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Data Sources */}
        <div className="data-sources">
          <p>📡 <strong>Live data from:</strong> Open-Meteo API (weather & marine), NOAA Global Monitoring Lab (CO₂), NASA GISTEMP (temperature), NSIDC (arctic ice), FAO (fisheries)</p>
          <p>Last updated: {new Date().toLocaleString('en-IN')}</p>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
